import { clearTimer } from '@gonfalon/timer/clearTimer';
import { startTimer } from '@gonfalon/timer/startTimer';
import { stopTimer } from '@gonfalon/timer/stopTimer';

import { createTrackerForCategory } from './createTrackerForCategory';
import type { AnalyticEventData } from './types';

type EventWithCategory<Category extends string, TimerName extends string> = `${Category}.${TimerName}`;

export interface MeasureOptions {
  throwErrorIfTimerNotStarted?: boolean;
}

/**
 * Creates a timed analytics tracker that combines performance timing with event tracking.
 *
 * This function creates a tracker that manages performance timers and automatically includes
 * duration metrics when tracking analytics events. Timer names are scoped to the provided
 * category to avoid naming collisions.
 *
 * @param category - The analytics category for all tracked events (e.g., 'FlagsTargeting', 'UserFlow')
 *
 * @returns A tracker function with the following methods:
 * - `(eventName, attributes?)` - Track an analytics event with optional attributes
 * - `start(timerName)` - Start a performance timer with the given name
 * - `stop(timerName)` - Stop a timer and return performance measures
 * - `measure(timerName)` - Builder pattern that stops a timer and returns:
 *   - `track(eventName, attributes?)` - Track an event with the timer's duration as metricValue
 *     - Returns `{ restart() }` - Optionally restart the timer
 *   - `restart()` - Restart the timer without tracking
 * - `measureAndTrack(eventName, attributes?)` - Convenience method that measures and tracks in one call
 *   - Use when the event name matches the timer name
 *   - Returns `{ restart() }` - Optionally restart the timer
 * - `clear(timerName?)` - Clear a specific timer or all timers if no name provided
 *
 * @example
 * ```typescript
 * // Create a tracker for flag targeting events
 * const trackFlagEvent = createTimedTracker('FlagsTargeting');
 *
 * // Start a timer when component mounts
 * trackFlagEvent.start('TIME_TO_SAVE');
 *
 * // Option 1: Measure and track with a different event name + timer name
 * trackFlagEvent
 *   .measure('TIME_TO_SAVE')
 *   .track('Save Button Clicked', { flagKey, saveOption })
 *   .restart();
 *
 * // Option 2: Measure and track with the same timer + event name (convenience method)
 * trackFlagEvent
 *   .measureAndTrack('TIME_TO_SAVE', { flagKey, saveOption })
 *   .restart();
 *
 * // Clean up timer on unmount
 * trackFlagEvent.clear('TIME_TO_SAVE');
 * ```
 *
 */
export function createTimedTracker<Category extends string, TimerName extends string>(category: Category) {
  type E = EventWithCategory<Category, TimerName>;
  const events = new Set<E>();
  const track = createTrackerForCategory(category);

  const getEventWithCategory = (eventName: TimerName): E => `${category}.${eventName}`;

  // Wrap events with the category to avoid clashing
  const wrapWithCategory = <P extends unknown[], R = void>(fn: (eventName: E, ...rest: P) => R) => {
    return (eventName: TimerName, ...rest: P) => {
      const eventWithCategory = getEventWithCategory(eventName);
      return fn(eventWithCategory, ...rest);
    };
  };

  const start = wrapWithCategory((eventName: E) => {
    events.add(eventName);
    startTimer(eventName);

    return {
      track,
    };
  });

  const stop = wrapWithCategory((eventName: E) => {
    events.delete(eventName);
    return stopTimer(eventName);
  });

  const measure = (timerName: TimerName, options: MeasureOptions = { throwErrorIfTimerNotStarted: true }) => {
    if (!events.has(getEventWithCategory(timerName)) && __DEV__ && options.throwErrorIfTimerNotStarted) {
      throw new Error(`Timer ${timerName} not started`);
    }

    const performanceMeasures = stop(timerName);

    const restart = () => start(timerName);

    return {
      measure,
      track: (eventName: string, attributes?: AnalyticEventData) => {
        performanceMeasures.forEach((entry) => {
          track(eventName, {
            ...attributes,
            metricValue: entry.duration,
          });
        });

        return {
          restart,
        };
      },
      restart,
    };
  };

  const measureAndTrack = (
    eventName: TimerName,
    options: AnalyticEventData & MeasureOptions = { throwErrorIfTimerNotStarted: true },
  ) => {
    const { throwErrorIfTimerNotStarted = true, ...restAttributes } = options ?? {};
    const { track: trackEvent } = measure(eventName, options);

    return trackEvent(eventName, restAttributes);
  };

  const clearEvent = wrapWithCategory((eventName: E) => {
    events.delete(eventName);
    clearTimer(eventName);
  });

  const clear = (eventName?: TimerName) => {
    if (eventName) {
      clearEvent(eventName);
    } else {
      for (const e of events) {
        clearTimer(e);
      }
      events.clear();
    }
  };

  return Object.assign(track, {
    start,
    stop,
    measure,
    clear,
    measureAndTrack,
  });
}
